import { Injectable } from "@angular/core";
import { ModulesResource } from "core/stages-client";
import { ActivatedRoute, Router } from "@angular/router";

type JobIdentifier = stages.core.scheduler.JobIdentifier;
type ModuleInstallation = stages.management.modules.ModuleInstallation;

@Injectable({ providedIn: "root" })
export class ModuleService {
	constructor(private readonly modulesResource: ModulesResource) {}

	async getModules(
		workspaceId: string,
		pv: string,
		page: number,
		pageSize: number,
		isPolling: boolean,
	): Promise<SecuredPagedResult<ModuleInstallation>> {
		if (isPolling) {
			this.modulesResource.setHeadersForNextRequest({
				"Stages-Polling": "true",
			});
		}
		return this.modulesResource.getInstallations(workspaceId, {
			pv: pv,
			page: page,
			pageSize: pageSize,
		});
	}

	async getUsage(
		workspaceId: string,
		pv: string,
		page: number,
		pageSize: number,
		isPolling: boolean,
	): Promise<SecuredPagedResult<ModuleInstallation>> {
		if (isPolling) {
			this.modulesResource.setHeadersForNextRequest({
				"Stages-Polling": "true",
			});
		}
		return this.modulesResource.getUsage(workspaceId, {
			pv: pv,
			page: page,
			pageSize: pageSize,
		});
	}

	async pullUpdateModules(workspaceId: string, pv: string, moduleInstallationIds: string[]): Promise<JobIdentifier[]> {
		return this.modulesResource.pullUpdate(workspaceId, moduleInstallationIds, {
			pv: pv,
		});
	}

	async removeModules(
		workspaceId: string,
		pv: string,
		moduleInstallationIds: string[],
		page: number,
		pageSize = 10,
	): Promise<SecuredPagedResult<ModuleInstallation>> {
		return this.modulesResource.remove(workspaceId, moduleInstallationIds, {
			pv: pv,
			page: page,
			pageSize: pageSize,
		});
	}

	async setOnDemand(workspaceId: string, pv: string, moduleInstallationId: string, onDemand: boolean): Promise<void> {
		return this.modulesResource.setOnDemand(workspaceId, moduleInstallationId, onDemand, {
			pv: pv,
		});
	}

	async setPushUpdatesEnabled(
		workspaceId: string,
		pv: string,
		moduleInstallationId: string,
		pushUpdatesEnabled: boolean,
	): Promise<void> {
		return this.modulesResource.setPushUpdatesEnabled(workspaceId, moduleInstallationId, pushUpdatesEnabled, {
			pv: pv,
		});
	}

	async stop(workspaceId: string, jobNames: string[]): Promise<void> {
		return this.modulesResource.stop(workspaceId, jobNames);
	}

	openDetailsPopup(module: ModuleInstallation, router: Router, route: ActivatedRoute, tabName: string): void {
		router.navigate(
			[
				"..",
				"progress",
				{
					jobName: module.jobName,
					jobType: module.jobType,
					tabName: tabName,
					messageKeyPrefix: this.getMessageKeyPrefix(module.jobType!),
				},
			],
			{ relativeTo: route },
		);
	}

	async clearWarnings(workspaceId: string, jobType: string, jobName: string): Promise<void> {
		await this.modulesResource.clearWarnings(workspaceId, jobName, jobType);
	}

	getMessageKeyPrefix(jobType: string): string {
		let messageKeyPrefix = "process.modules.";
		switch (jobType) {
			case "ADD_MODULE": {
				messageKeyPrefix += "add";
				break;
			}
			case "UPDATE_MODULE": {
				messageKeyPrefix += "update";
				break;
			}
			case "REMOVE_MODULE": {
				messageKeyPrefix += "remove";
				break;
			}
			default: {
				messageKeyPrefix += "unknown";
			}
		}
		return messageKeyPrefix;
	}
}
